// src/realityCheck/RealityCheckModal.tsx
//
// BekalNanti — Milestone P6: Shared Reality Check Layer Modal
// 100% Deterministic Evaluation Presentation. Zero AI, zero developer jargon.
// Explains the honest trajectory of the plan against household reality.

import React from 'react'
import {
  X,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  HelpCircle,
  CheckCircle2,
  Users,
  Wallet,
  TrendingUp,
  ArrowRight,
  Info,
} from 'lucide-react'
import { GoalRecord, JourneyData } from '../journey/types'
import { evaluatePlanReality } from '../lib/realityCheckService'
import { RealityCheckStatus } from '../lib/realityCheckTypes'
import { formatIDR } from '../lib/financialCalculation'

interface RealityCheckModalProps {
  isOpen: boolean
  onClose: () => void
  goal: GoalRecord | null
  journey: JourneyData
  onOpenCostFunding?: () => void
  onOpenCalculation?: () => void
}

export const RealityCheckModal: React.FC<RealityCheckModalProps> = ({
  isOpen,
  onClose,
  goal,
  journey,
  onOpenCostFunding,
  onOpenCalculation,
}) => {
  if (!isOpen || !goal) return null

  const evaluation = evaluatePlanReality(goal, journey)
  const { snapshot, status, signals, uncertainties, assumptions, missingInformation } = evaluation

  // Badge Styling based on status
  const getStatusBadge = (st: RealityCheckStatus, label: string) => {
    switch (st) {
      case 'fully_funded':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={13} className="text-emerald-600" />
            {label}
          </span>
        )
      case 'manageable_commitment':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">
            <ShieldCheck size={13} className="text-teal-600" />
            {label}
          </span>
        )
      case 'funding_pressure':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={13} className="text-amber-600" />
            {label}
          </span>
        )
      case 'funding_gap':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <AlertCircle size={13} className="text-rose-600" />
            {label}
          </span>
        )
      case 'insufficient_information':
      case 'not_started':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
            <HelpCircle size={13} className="text-slate-500" />
            {label}
          </span>
        )
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reality-check-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden text-left">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between bg-slate-50/60">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-teal-50 border border-teal-200/80 flex items-center justify-center text-[#0F766E] flex-shrink-0 mt-0.5">
              <ShieldCheck size={20} strokeWidth={2.4} />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="reality-check-title" className="text-base sm:text-lg font-bold text-slate-900">
                  Evaluasi Kesiapan Nyata
                </h2>
                {getStatusBadge(status, evaluation.statusLabel)}
              </div>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold text-slate-700">{goal.name}</span>
                <span>•</span>
                <span>
                  Untuk: <strong>{evaluation.personName}</strong>
                  {evaluation.personRelationship ? ` (${evaluation.personRelationship})` : ''}
                </span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup modal evaluasi kesiapan"
            className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {/* Section 1: Headline & Summary Statement */}
          <div
            className={`p-5 rounded-2xl border transition-all ${
              status === 'fully_funded'
                ? 'bg-emerald-50/60 border-emerald-200'
                : status === 'manageable_commitment'
                ? 'bg-teal-50/60 border-teal-200'
                : status === 'funding_pressure'
                ? 'bg-amber-50/60 border-amber-200'
                : status === 'funding_gap'
                ? 'bg-rose-50/60 border-rose-200'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                {status === 'fully_funded' && <CheckCircle2 size={20} className="text-emerald-600" />}
                {status === 'manageable_commitment' && <ShieldCheck size={20} className="text-teal-600" />}
                {status === 'funding_pressure' && <AlertTriangle size={20} className="text-amber-600" />}
                {status === 'funding_gap' && <AlertCircle size={20} className="text-rose-600" />}
                {(status === 'insufficient_information' || status === 'not_started') && (
                  <HelpCircle size={20} className="text-slate-500" />
                )}
              </div>
              <div className="space-y-1 flex-1">
                <h3 className="text-sm font-bold text-slate-900">{evaluation.headline}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{evaluation.summaryStatement}</p>
              </div>
            </div>

            {/* Quick Metrics Bar (if calculable) */}
            {evaluation.isCalculable && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-200/60">
                <div className="bg-white/80 p-3 rounded-xl border border-slate-200/60">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                    Proyeksi Biaya
                  </div>
                  <div className="text-sm sm:text-base font-bold text-slate-900 mt-0.5">
                    {formatIDR(snapshot.totalProjectedCost)}
                  </div>
                </div>
                <div className="bg-white/80 p-3 rounded-xl border border-slate-200/60">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                    Dana Pasti Tersedia
                  </div>
                  <div className="text-sm sm:text-base font-bold text-emerald-700 mt-0.5">
                    {formatIDR(snapshot.confirmedFunding)}
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1 bg-white/80 p-3 rounded-xl border border-slate-200/60">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                    Sisa Gap Pasti
                  </div>
                  <div
                    className={`text-sm sm:text-base font-bold mt-0.5 ${
                      snapshot.confirmedFundingGap === 0 ? 'text-emerald-700' : 'text-rose-700'
                    }`}
                  >
                    {formatIDR(snapshot.confirmedFundingGap)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Yang Sudah Pasti vs Estimasi (Pemisahan Tegas) */}
          {evaluation.isCalculable && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <Wallet size={14} className="text-[#0F766E]" />
                Kepastian Sumber Pendanaan
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Dana Pasti */}
                <div className="p-4 rounded-2xl bg-emerald-50/40 border border-emerald-200/80 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                      <ShieldCheck size={14} className="text-emerald-600" />
                      Dana Pasti (Terkonfirmasi)
                    </span>
                    <span className="text-xs font-extrabold text-emerald-700">
                      {formatIDR(snapshot.confirmedFunding)}
                    </span>
                  </div>
                  <p className="text-[11px] text-emerald-950/80 leading-relaxed">
                    Hanya dana dengan kepastian jelas (tabungan khusus/investasi pasti) yang dihitung sebagai modal utama pelindung rencana.
                  </p>
                </div>

                {/* Potensi Estimasi */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <TrendingUp size={14} className="text-teal-600" />
                      Potensi Dana / Beasiswa
                    </span>
                    <span className="text-xs font-extrabold text-slate-700">
                      {formatIDR(snapshot.estimatedFunding)}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    {snapshot.estimatedFunding > 0
                      ? `Jika dana estimasi ini berhasil diperoleh, sisa gap berkurang menjadi ${formatIDR(snapshot.gapAfterEstimatedFunding)}.`
                      : 'Belum ada potensi beasiswa atau bantuan terencana yang dicatat.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section 3: Tekanan & Kapasitas Arus Kas Bulanan */}
          {evaluation.isCalculable && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <TrendingUp size={14} className="text-[#0F766E]" />
                Kapasitas Arus Kas & Tabungan Bulanan
              </h4>
              <div className="p-4 rounded-2xl bg-white border border-slate-200 space-y-3 shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b border-slate-100">
                  <div>
                    <div className="text-[11px] text-slate-500">Kebutuhan Tabungan Bulanan:</div>
                    <div className="text-base font-extrabold text-slate-900 mt-0.5">
                      {formatIDR(snapshot.requiredMonthlySavingConfirmedGap)}
                      <span className="text-xs font-normal text-slate-500"> / bulan</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Untuk menutup gap pasti selama {snapshot.yearsUntilTarget} tahun ({snapshot.monthsUntilTarget} bulan)
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500">Kapasitas Bersih Keluarga:</div>
                    <div className="text-base font-extrabold text-[#0F766E] mt-0.5">
                      {formatIDR(snapshot.householdNetCapacity)}
                      <span className="text-xs font-normal text-slate-500"> / bulan</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Sisa pendapatan setelah pengeluaran rutin dan cicilan
                    </div>
                  </div>
                </div>

                {/* Surplus / Deficit Callout */}
                <div
                  className={`p-3 rounded-xl text-xs flex items-center justify-between ${
                    snapshot.hasCapacityDeficit
                      ? 'bg-rose-50 text-rose-800 border border-rose-200'
                      : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                  }`}
                >
                  <span className="font-semibold">
                    {snapshot.hasCapacityDeficit ? 'Defisit Arus Kas Bulanan:' : 'Sisa Ruang Aman Arus Kas:'}
                  </span>
                  <span className="font-extrabold">
                    {formatIDR(Math.abs(snapshot.monthlyCapacityDifference))} / bulan
                  </span>
                </div>

                {/* Multi-Goal Household Notice */}
                {snapshot.otherActiveGoalsCount > 0 && (
                  <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-200/80 flex items-start gap-2.5">
                    <Users size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-[11px] text-amber-900 leading-relaxed">
                      <strong>Catatan Kapasitas Bersama:</strong> Kapasitas arus kas bulanan ({formatIDR(snapshot.householdNetCapacity)}) merupakan kapasitas bersama keluarga dan saat ini juga digunakan bersama <strong>{snapshot.otherActiveGoalsCount} rencana aktif keluarga lainnya</strong>.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sinyal Trajektori Nyata */}
          {signals.length > 0 && (
            <div className="space-y-2.5">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#0F766E]" />
                Sinyal Trajektori Perencanaan
              </h4>
              <div className="space-y-2">
                {signals.map((sig) => (
                  <div
                    key={sig.id}
                    className={`p-3 rounded-xl border text-xs flex items-start gap-2.5 ${
                      sig.severity === 'positive'
                        ? 'bg-emerald-50/50 border-emerald-200 text-emerald-950'
                        : sig.severity === 'warning'
                        ? 'bg-rose-50/50 border-rose-200 text-rose-950'
                        : sig.severity === 'attention'
                        ? 'bg-amber-50/50 border-amber-200 text-amber-950'
                        : 'bg-slate-50 border-slate-200 text-slate-800'
                    }`}
                  >
                    <div className="mt-0.5 flex-shrink-0">
                      {sig.severity === 'positive' && <CheckCircle2 size={14} className="text-emerald-600" />}
                      {sig.severity === 'warning' && <AlertCircle size={14} className="text-rose-600" />}
                      {sig.severity === 'attention' && <AlertTriangle size={14} className="text-amber-600" />}
                      {sig.severity === 'neutral' && <Info size={14} className="text-teal-600" />}
                    </div>
                    <div className="space-y-0.5">
                      <div className="font-bold text-[12px]">{sig.title}</div>
                      <div className="text-[11px] leading-relaxed opacity-90">{sig.statement}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 4: Hal yang Belum Pasti (Uncertainties) & Asumsi */}
          {(uncertainties.length > 0 || assumptions.length > 0 || missingInformation.length > 0) && (
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <Info size={14} className="text-[#0F766E]" />
                Asumsi Perhitungan & Hal yang Belum Pasti
              </h4>

              {/* Uncertainties Cards */}
              {uncertainties.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase">
                    Ketidakpastian yang Perlu Diperhatikan:
                  </div>
                  {uncertainties.map((u) => (
                    <div
                      key={u.id}
                      className="p-3 rounded-xl bg-amber-50/60 border border-amber-200 text-xs space-y-1"
                    >
                      <div className="font-bold text-amber-900 flex items-center gap-1.5">
                        <AlertTriangle size={13} className="text-amber-600" />
                        {u.title}
                      </div>
                      <p className="text-[11px] text-amber-800 leading-relaxed">{u.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Missing Information Cards */}
              {missingInformation.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase">
                    Informasi Kunci yang Belum Dilengkapi:
                  </div>
                  {missingInformation.map((m) => (
                    <div
                      key={m.id}
                      className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-1"
                    >
                      <div className="font-bold text-slate-800">{m.field}</div>
                      <p className="text-[11px] text-slate-600 leading-relaxed">{m.impactOnEvaluation}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Assumptions List */}
              {assumptions.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase">
                    Asumsi yang Digunakan:
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {assumptions.map((a) => (
                      <div key={a.id} className="p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
                        <div className="text-[10px] text-slate-400 uppercase font-semibold">{a.name}</div>
                        <div className="text-xs font-bold text-slate-900">{a.assumedValue}</div>
                        <p className="text-[10px] text-slate-500 leading-tight">{a.sensitivityNote}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-200/60 transition-colors cursor-pointer"
          >
            Tutup
          </button>

          <div className="flex items-center gap-2">
            {onOpenCostFunding && (
              <button
                type="button"
                onClick={() => {
                  onClose()
                  onOpenCostFunding()
                }}
                className="px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 shadow-sm transition-all cursor-pointer"
              >
                Ubah Biaya & Dana
              </button>
            )}

            {onOpenCalculation && (
              <button
                type="button"
                onClick={() => {
                  onClose()
                  onOpenCalculation()
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#0EA5A4] hover:bg-[#0F766E] shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <span>Lihat Perhitungan Rinci</span>
                <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
